import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Building2, Users, GraduationCap, BookOpen, Palette, Shield,
  CheckCircle, XCircle, Clock, Plus, Copy, LogOut, ChevronRight,
  LayoutDashboard, UserCheck, AlertCircle, RefreshCw, Upload, Trash2,
  ExternalLink, Globe, Settings, Info, Link2, Paintbrush, Check, Lock,
  Search, Command, ArrowRight, X, Receipt, FileText, Monitor,
  Briefcase, Sparkles, Key, Library, MessageCircle
} from '@/components/DeskeyIcons';
import { DASHBOARD_THEMES, LOGIN_THEMES } from '@/lib/themes';
import DepartmentsSection from '@/components/dashboard/DepartmentsSection';
import { TeachersSection } from '@/components/dashboard/TeachersSection';
import { StudentsSection } from '@/components/dashboard/StudentsSection';
import { LicensingSection } from '@/components/dashboard/LicensingSection';
import { InvoicesSection } from '@/components/dashboard/InvoicesSection';
import { TeacherDeskSection } from '@/components/dashboard/TeacherDeskSection';
import { DeviceManagementSection } from '@/components/dashboard/DeviceManagementSection';
import { DeskeyHubSection } from '@/components/dashboard/DeskeyHubSection';
import { SupportTicketsSection } from '@/components/dashboard/SupportTicketsSection';
import { SubjectManagement } from '@/SubjectManagement';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DESKEY_GREEN = '#34A853';
const DeskyLogo = () => (
  <svg width="32" height="32" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.5 7.2C11.5 4.1 15 2.2 17.6 3.9L34.2 15.7C36.5 17.3 36.5 20.7 34.2 22.3L17.6 34.1C15 35.8 11.5 33.9 11.5 30.8V7.2Z" fill="#34A853" />
  </svg>
);

const Toggle = ({ checked, onChange, testId, accent }) => (
  <button onClick={() => onChange(!checked)} className="relative w-11 h-6 rounded-full transition-colors" style={{ background: checked ? accent : '#d1d5db' }} data-testid={testId}>
    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'left-6' : 'left-1'}`} />
  </button>
);

const SectionTitle = ({ title, desc }) => <div className="mb-6"><h3 className="text-xl font-extrabold tracking-tight text-gray-900" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>{title}</h3>{desc && <p className="text-sm text-gray-500 mt-1 font-medium" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{desc}</p>}</div>;
const Card = ({ children, className = '' }) => <div className={`bg-white border border-gray-200 rounded-xl p-6 transition-all duration-200 hover:shadow-md ${className}`} style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)' }}>{children}</div>;

const MENU = [
  { id: 'overview', label: 'Overview', icon: LayoutDashboard },
  { id: 'departments', label: 'Departments & Classes', icon: Building2 },
  { id: 'licensing', label: 'Licensing', icon: UserCheck },
  { id: 'invoices', label: 'Invoices', icon: Receipt },
  { id: 'devices', label: 'Devices', icon: Monitor },
  { id: 'teacher-desk', label: 'Teacher Desk', icon: FileText },
  { id: 'deskey-hub', label: 'DeskeyHUB', icon: Library },
  { id: 'teachers', label: 'Teachers', icon: BookOpen },
  { id: 'students', label: 'Students', icon: GraduationCap },
  { id: 'support-tickets', label: 'Support Tickets', icon: MessageCircle },
  { id: 'settings', label: 'Settings', icon: Settings },
  { id: 'security', label: 'Security', icon: Shield },
];

const SETTINGS_TABS = [
  { id: 'general', label: 'General Info' },
  { id: 'appearance', label: 'Appearance' },
  { id: 'branding', label: 'Login Page' },
  { id: 'domain', label: 'Domain' },
];

/* ── Spotlight / Command Palette ── */
const SEARCH_ITEMS = [
  { id: 'overview', label: 'Overview', desc: 'Dashboard stats & quick info', icon: LayoutDashboard, type: 'page' },
  { id: 'licensing', label: 'Licensing', desc: 'Import, invite & manage users', icon: UserCheck, type: 'page' },
  { id: 'teachers', label: 'Teachers', desc: 'Manage teacher accounts', icon: BookOpen, type: 'page' },
  { id: 'students', label: 'Students', desc: 'Manage student accounts', icon: GraduationCap, type: 'page' },
  { id: 'departments', label: 'Departments', desc: 'Manage departments & classes', icon: Building2, type: 'page' },
  { id: 'devices', label: 'Devices', desc: 'Manage Desky consoles & wearables', icon: Monitor, type: 'page' },
  { id: 'support-tickets', label: 'Support Tickets', desc: 'Raise and track support tickets', icon: MessageCircle, type: 'page' },
  { id: 'settings', label: 'Settings', desc: 'General, appearance, branding', icon: Settings, type: 'page' },
  { id: 'security', label: 'Security', desc: '2FA & security policies', icon: Shield, type: 'page' },
  { id: 'action_change_theme', label: 'Change Theme', desc: 'Customize dashboard appearance', icon: Palette, type: 'action', navigateTo: 'settings' },
  { id: 'action_view_login', label: 'Preview Login Page', desc: 'See branded tenant login', icon: ExternalLink, type: 'action', navigateTo: 'settings' },
  { id: 'action_manage_classes', label: 'Manage Classes', desc: 'View classes inside departments', icon: BookOpen, type: 'action', navigateTo: 'departments' },
  { id: 'action_import_users', label: 'Import Users', desc: 'Bulk import via CSV', icon: Upload, type: 'action', navigateTo: 'licensing' },
  { id: 'action_send_invite', label: 'Send Invite', desc: 'Email invite with OTP', icon: UserCheck, type: 'action', navigateTo: 'licensing' },
];

function SpotlightSearch({ onNavigate, accent }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const A = accent;

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setOpen(true); }
      if (e.key === 'Escape') { setOpen(false); setQuery(''); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
    if (open) { setQuery(''); setActiveIdx(0); }
  }, [open]);

  const filtered = query.trim()
    ? SEARCH_ITEMS.filter(i => i.label.toLowerCase().includes(query.toLowerCase()) || i.desc.toLowerCase().includes(query.toLowerCase()))
    : SEARCH_ITEMS.filter(i => i.type === 'page');

  const handleSelect = (item) => {
    onNavigate(item.navigateTo || item.id);
    setOpen(false);
    setQuery('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, filtered.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && filtered[activeIdx]) { handleSelect(filtered[activeIdx]); }
  };

  useEffect(() => { setActiveIdx(0); }, [query]);

  return (
    <>
      {/* Trigger button in header — hidden when modal is open */}
      {!open && (
        <button onClick={() => setOpen(true)} className="hidden sm:flex items-center gap-2.5 h-9 pl-3.5 pr-3 rounded-xl border border-gray-200 bg-gray-50/60 hover:bg-gray-100 hover:border-gray-300 transition-all text-sm text-gray-500 min-w-[240px] shadow-[0_1px_2px_rgba(0,0,0,0.03)]" data-testid="spotlight-trigger">
          <Search className="w-3.5 h-3.5 text-gray-500" strokeWidth={2} />
          <span className="text-[13px] flex-1 text-left text-gray-500/80" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Search pages, actions...</span>
          <kbd className="hidden lg:inline-flex items-center gap-0.5 text-[10px] text-gray-500 bg-white border border-gray-200 rounded-md px-1.5 py-0.5 font-mono shadow-[0_1px_0_rgba(0,0,0,0.04)]">
            <Command className="w-2.5 h-2.5" />K
          </kbd>
        </button>
      )}

      {/* Mobile trigger — hidden when modal is open */}
      {!open && (
        <button onClick={() => setOpen(true)} className="sm:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors" data-testid="spotlight-trigger-mobile">
          <Search className="w-4 h-4 text-gray-500" strokeWidth={1.75} />
        </button>
      )}

      {/* Spotlight overlay */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]" data-testid="spotlight-modal">
          <div className="absolute inset-0 bg-white/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-150 border border-gray-200">
            {/* Search input */}
            <div className="flex items-center gap-3 px-4 h-14 border-b border-gray-100">
              <Search className="w-5 h-5 text-gray-500 flex-shrink-0" strokeWidth={1.75} />
              <input
                ref={inputRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search pages, actions..."
                className="flex-1 text-[15px] text-gray-900 placeholder:text-gray-500 outline-none bg-transparent"
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                data-testid="spotlight-input"
              />
              <button onClick={() => { setOpen(false); setQuery(''); }} className="w-7 h-7 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors flex-shrink-0" data-testid="spotlight-close">
                <X className="w-3.5 h-3.5 text-gray-600" strokeWidth={2} />
              </button>
            </div>

            {/* Results */}
            <div className="max-h-[380px] overflow-y-auto py-2" data-testid="spotlight-results">
              {filtered.length === 0 ? (
                <div className="px-4 py-10 text-center">
                  <Search className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No results for "{query}"</p>
                </div>
              ) : (
                <>
                  {query.trim() === '' && <p className="px-4 pt-1 pb-2 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Pages</p>}
                  {filtered.map((item, i) => {
                    const isActive = i === activeIdx;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleSelect(item)}
                        onMouseEnter={() => setActiveIdx(i)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${isActive ? 'bg-gray-50' : 'hover:bg-gray-50/60'}`}
                        data-testid={`spotlight-item-${item.id}`}
                      >
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? '' : 'bg-gray-100/60'}`} style={isActive ? { background: `${A}14` } : {}}>
                          <item.icon className="w-[17px] h-[17px] transition-colors" style={{ color: isActive ? A : '#94a3b8' }} strokeWidth={1.75} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-[13px] font-medium truncate ${isActive ? 'text-gray-900' : 'text-gray-800'}`}>{item.label}</p>
                          <p className="text-[11px] text-gray-500 truncate">{item.desc}</p>
                        </div>
                        {isActive && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className="text-[10px] text-gray-500">Go</span>
                            <ArrowRight className="w-3.5 h-3.5 text-gray-500" />
                          </div>
                        )}
                        {item.type === 'action' && !isActive && (
                          <span className="text-[9px] font-semibold text-gray-500 bg-gray-100 rounded px-1.5 py-0.5 uppercase tracking-wider flex-shrink-0">Action</span>
                        )}
                      </button>
                    );
                  })}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2.5 border-t border-gray-100 flex items-center gap-4 text-[10px] text-gray-500">
              <span className="flex items-center gap-1"><kbd className="bg-gray-100 border border-gray-200 rounded px-1 py-0.5 font-mono text-[9px]">↑↓</kbd> Navigate</span>
              <span className="flex items-center gap-1"><kbd className="bg-gray-100 border border-gray-200 rounded px-1 py-0.5 font-mono text-[9px]">↵</kbd> Open</span>
              <span className="flex items-center gap-1"><kbd className="bg-gray-100 border border-gray-200 rounded px-1 py-0.5 font-mono text-[9px]">esc</kbd> Close</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function api(path, opts = {}) {
  const token = localStorage.getItem('desky_token');
  const headers = { 'Authorization': `Bearer ${token}`, ...opts.headers };
  if (!(opts.body instanceof FormData)) headers['Content-Type'] = 'application/json';
  return fetch(`${API_URL}/api${path}`, { headers, ...opts }).then(r => r.json());
}

export default function InstitutionDashboard() {
  const nav = useNavigate();
  const [section, setSection] = useState('overview');
  const [settingsTab, setSettingsTab] = useState('general');
  const [dashboard, setDashboard] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentDepts, setStudentDepts] = useState([]);
  const [studentBatches, setStudentBatches] = useState([]);
  const [licensingDepts, setLicensingDepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [branding, setBranding] = useState({});
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [twoFA, setTwoFA] = useState({ institution: true, teacher: true, student: false });
  const [twofaSaving, setTwofaSaving] = useState(false);
  const [general, setGeneral] = useState({});
  const [generalSaving, setGeneralSaving] = useState(false);
  const [domain, setDomain] = useState('');
  const [domainSaving, setDomainSaving] = useState(false);
  const [domainVerifying, setDomainVerifying] = useState(false);
  const [toast, setToast] = useState('');

  // Theme
  const [dashTheme, setDashTheme] = useState(DASHBOARD_THEMES[0]);
  const [selectedThemeId, setSelectedThemeId] = useState('desky-green');
  const [isCustomTheme, setIsCustomTheme] = useState(false);
  const [customSidebar, setCustomSidebar] = useState('#14532d');
  const [customAccent, setCustomAccent] = useState('#34A853');
  const [themeSaving, setThemeSaving] = useState(false);

  // Settings 2FA gate
  const [settingsUnlocked, setSettingsUnlocked] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFASending, setTwoFASending] = useState(false);
  const [twoFAVerifying, setTwoFAVerifying] = useState(false);
  const [twoFADevCode, setTwoFADevCode] = useState('');
  const [twoFASent, setTwoFASent] = useState(false);

  // Save 2FA
  const [pendingSave, setPendingSave] = useState(null);
  const [pendingSaveId, setPendingSaveId] = useState(null);
  const [saveCode, setSaveCode] = useState('');
  const [saveDevCode, setSaveDevCode] = useState('');
  const [saveVerifying, setSaveVerifying] = useState(false);

  useEffect(() => {
    const u = localStorage.getItem('desky_user');
    const t = localStorage.getItem('desky_token');
    if (!u || !t) { nav('/mydeskey'); return; }
    const parsed = JSON.parse(u);
    if (!['tenant_owner', 'tenant_admin'].includes(parsed.role)) { nav('/'); return; }
    setUser(parsed);
    // Pre-load cached theme to prevent flash
    try {
      const cachedTenant = JSON.parse(localStorage.getItem('desky_tenant') || '{}');
      if (cachedTenant.dashboard_theme) {
        const saved = cachedTenant.dashboard_theme;
        if (saved.theme_id === 'custom' && saved.custom_colors) {
          setIsCustomTheme(true); setSelectedThemeId('custom');
          setCustomSidebar(saved.custom_colors.sidebar || '#14532d');
          setCustomAccent(saved.custom_colors.accent || '#34A853');
          setDashTheme({ sidebar: saved.custom_colors.sidebar || '#14532d', accent: saved.custom_colors.accent || '#34A853' });
        } else {
          const found = DASHBOARD_THEMES.find(th => th.id === saved.theme_id);
          if (found) { setSelectedThemeId(found.id); setDashTheme(found); }
        }
      }
    } catch {}
    loadDashboard();
  }, [nav]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api('/institution/dashboard');
      setDashboard(d);
      if (d.branding) setBranding(d.branding);
      if (d.tenant?.['2fa_config']) setTwoFA(d.tenant['2fa_config']);
      setGeneral({ name: d.tenant?.name, institution_type: d.tenant?.tenant_type, phone: d.tenant?.phone, website: d.tenant?.website, address: d.tenant?.address });
      if (d.tenant?.custom_domain) setDomain(d.tenant.custom_domain);
      if (d.tenant?.dashboard_theme) {
        const saved = d.tenant.dashboard_theme;
        if (saved.theme_id === 'custom' && saved.custom_colors) {
          setIsCustomTheme(true); setSelectedThemeId('custom');
          setCustomSidebar(saved.custom_colors.sidebar || '#14532d');
          setCustomAccent(saved.custom_colors.accent || '#34A853');
          setDashTheme({ sidebar: saved.custom_colors.sidebar || '#14532d', accent: saved.custom_colors.accent || '#34A853' });
        } else {
          const found = DASHBOARD_THEMES.find(t => t.id === saved.theme_id);
          if (found) { setSelectedThemeId(found.id); setDashTheme(found); }
        }
      }
      // Cache tenant with theme for instant load
      if (d.tenant) {
        try { localStorage.setItem('desky_tenant', JSON.stringify(d.tenant)); } catch {}
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const loadApprovals = useCallback(async () => {}, []);
  const loadTeachers = useCallback(async () => { setTeachers((await api('/institution/teachers')).teachers || []); }, []);
  const loadStudents = useCallback(async () => { const d = await api('/institution/students'); setStudents(d.students || []); setStudentDepts(d.departments || []); setStudentBatches(d.batches || []); }, []);
  const loadLicensingDepts = useCallback(async () => {
    try { const d = await api('/institution/students'); setLicensingDepts(d.departments || []); } catch {}
  }, []);

  useEffect(() => {
    if (section === 'licensing') loadLicensingDepts();
    if (section === 'teachers') loadTeachers();
    if (section === 'students') loadStudents();
  }, [section, loadLicensingDepts, loadTeachers, loadStudents]);

  const handleApproval = async (id, action) => { await api('/institution/approve', { method: 'POST', body: JSON.stringify({ request_id: id, action }) }); loadApprovals(); loadDashboard(); };
  const handleRevoke = async (uid) => { if (!window.confirm('Revoke this user\'s licence?')) return; await api('/institution/revoke-licence', { method: 'POST', body: JSON.stringify({ user_id: uid }) }); loadTeachers(); loadStudents(); loadDashboard(); };

  // ─── 2FA GATE ───
  const sendSettingsCode = async () => {
    setTwoFASending(true);
    const r = await api('/auth/2fa/send', { method: 'POST', body: JSON.stringify({ email: user?.email }) });
    setTwoFADevCode(r.dev_code || '');
    setTwoFASent(true); setTwoFASending(false);
  };

  const verifySettingsCode = async () => {
    setTwoFAVerifying(true);
    const r = await api('/auth/2fa/verify', { method: 'POST', body: JSON.stringify({ email: user?.email, code: twoFACode }) });
    if (r.verified) { setSettingsUnlocked(true); showToast('Settings unlocked'); }
    else showToast('Invalid code');
    setTwoFAVerifying(false);
  };

  // ─── 2FA SAVE CONFIRMATION ───
  const initSave2FA = async (saveAction, saveId) => {
    setPendingSave(() => saveAction);
    setPendingSaveId(saveId);
    const r = await api('/auth/2fa/send', { method: 'POST', body: JSON.stringify({ email: user?.email }) });
    setSaveDevCode(r.dev_code || '');
    setSaveCode('');
  };

  const confirmSave = async () => {
    setSaveVerifying(true);
    const r = await api('/auth/2fa/verify', { method: 'POST', body: JSON.stringify({ email: user?.email, code: saveCode }) });
    if (r.verified && pendingSave) { await pendingSave(); }
    else if (!r.verified) showToast('Invalid code');
    setPendingSave(null); setPendingSaveId(null); setSaveCode(''); setSaveDevCode('');
    setSaveVerifying(false);
  };

  // Save handlers (called after 2FA confirm)
  const doGeneralSave = async () => { setGeneralSaving(true); await api('/institution/general', { method: 'PUT', body: JSON.stringify(general) }); setGeneralSaving(false); loadDashboard(); showToast('General settings saved'); };
  const doBrandingSave = async () => { setBrandingSaving(true); await api('/institution/tenant-config', { method: 'PUT', body: JSON.stringify({ primary_color: branding.primary_color, accent_color: branding.accent_color, bg_color: branding.bg_color, login_title: branding.login_title, login_subtitle: branding.login_subtitle, footer_text: branding.footer_text }) }); setBrandingSaving(false); showToast('Login page settings saved'); };
  const doThemeSave = async () => { setThemeSaving(true); const body = isCustomTheme ? { theme_id: 'custom', custom_colors: { sidebar: customSidebar, accent: customAccent } } : { theme_id: selectedThemeId }; await api('/institution/dashboard-theme', { method: 'PUT', body: JSON.stringify(body) }); setThemeSaving(false); loadDashboard(); showToast('Theme saved'); };
  const doDomainSave = async () => { setDomainSaving(true); await api('/institution/custom-domain', { method: 'POST', body: JSON.stringify({ custom_domain: domain }) }); setDomainSaving(false); loadDashboard(); showToast('Domain saved'); };

  const handleLogoUpload = async (file) => {
    if (!file || file.size > 2 * 1024 * 1024) { alert('Max 2MB'); return; }
    const fd = new FormData(); fd.append('file', file);
    const d = await api('/institution/upload-logo', { method: 'POST', body: fd });
    if (d.logo_url) { setBranding(p => ({ ...p, logo_url: d.logo_url })); showToast('Logo uploaded'); }
  };
  const handleLogoRemove = async () => { await api('/institution/remove-logo', { method: 'DELETE' }); setBranding(p => ({ ...p, logo_url: null })); showToast('Logo removed'); };
  const handleTwoFASave = async () => { setTwofaSaving(true); await api('/institution/2fa-config', { method: 'PUT', body: JSON.stringify({ require_2fa_institution: twoFA.institution, require_2fa_teacher: twoFA.teacher, require_2fa_student: twoFA.student }) }); setTwofaSaving(false); showToast('Security settings saved'); };
  const handleDomainVerify = async () => { setDomainVerifying(true); await api('/institution/verify-domain', { method: 'POST' }); setDomainVerifying(false); loadDashboard(); showToast('Domain verified!'); };
  const handleDomainRemove = async () => { if (!window.confirm('Remove custom domain?')) return; await api('/institution/custom-domain', { method: 'DELETE' }); setDomain(''); loadDashboard(); showToast('Domain removed'); };

  // Theme helpers
  const selectTheme = (theme) => { setSelectedThemeId(theme.id); setIsCustomTheme(false); setDashTheme(theme); };
  const selectCustom = () => { setIsCustomTheme(true); setSelectedThemeId('custom'); setDashTheme({ sidebar: customSidebar, accent: customAccent }); };
  const updateCustomSidebar = (c) => { setCustomSidebar(c); if (isCustomTheme) setDashTheme(p => ({ ...p, sidebar: c })); };
  const updateCustomAccent = (c) => { setCustomAccent(c); if (isCustomTheme) setDashTheme(p => ({ ...p, accent: c })); };
  const applyLoginPreset = (p) => { setBranding(b => ({ ...b, bg_color: p.bg_color, primary_color: p.primary_color, accent_color: p.accent_color })); showToast(`${p.name} theme applied`); };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };
  const copy = (t) => { navigator.clipboard.writeText(t); showToast('Copied!'); };
  const logout = () => { localStorage.clear(); nav('/mydeskey'); };

  if (loading || !dashboard) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: dashTheme.accent }} /></div>;

  const t = dashboard.tenant || {};
  const s = dashboard.stats || {};
  const A = dashTheme.accent;
  const isLoginPresetMatch = (lt) => branding.bg_color === lt.bg_color && branding.primary_color === lt.primary_color;

  // 2FA inline confirm renderer (called as function, not component, to preserve input focus)
  const renderSaveWithTwoFA = (label, onSave, saving, testId) => (
    pendingSaveId === testId ? (
      <div className="flex items-center gap-2 mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200">
        <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
        <Input value={saveCode} onChange={e => setSaveCode(e.target.value)} placeholder="6-digit code" className="h-8 w-32 text-sm rounded-lg" data-testid="save-2fa-input" />
        <Button onClick={confirmSave} disabled={saveVerifying || !saveCode} size="sm" className="h-8 px-3 text-xs rounded-lg text-white" style={{ background: A }}>Confirm</Button>
        <button onClick={() => { setPendingSave(null); setPendingSaveId(null); }} className="text-xs text-gray-400">Cancel</button>
        {saveDevCode && <span className="text-[10px] text-orange-500 ml-1">(Dev: {saveDevCode})</span>}
      </div>
    ) : (
      <Button onClick={() => initSave2FA(onSave, testId)} disabled={saving} className="mt-4 h-10 px-6 rounded-lg text-white text-sm font-semibold" style={{ background: A }} data-testid={testId}>{saving ? 'Saving...' : label}</Button>
    )
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="institution-dashboard">
      {toast && <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white text-sm px-5 py-3 rounded-xl shadow-2xl animate-in fade-in slide-in-from-right-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{toast}</div>}

      {/* ── SIDEBAR ── */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-[260px] flex flex-col transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} style={{ background: dashTheme.sidebar }} data-testid="sidebar">
        <div className="px-5 pt-6 pb-5">
          <div className="flex items-center gap-2.5 mb-7">
            <DeskyLogo />
            <span className="text-[18px] font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.03em' }}>Deskey</span>
          </div>

          <div className="flex items-center gap-3">
            {branding.logo_url ? (
              <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-white/10">
                <img src={branding.logo_url} alt="" className="w-full h-full object-contain p-0.5" />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: A }}>
                <span className="text-[16px] font-black text-white" style={{ fontFamily: 'Manrope, sans-serif' }}>{t.name?.charAt(0)}</span>
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-bold text-[14px] text-white truncate leading-none" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.01em' }}>{t.name}</p>
              <p className="text-[10px] text-white/30 truncate leading-none mt-1.5 font-medium uppercase tracking-[0.12em]">{t.institution_type || t.tenant_type}</p>
            </div>
          </div>
        </div>

        <div className="mx-5 border-t border-white/[0.06]" />

        {/* Navigation */}
        <nav className="flex-1 px-3 py-3 space-y-0.5 overflow-y-auto">
          {MENU.map(m => {
            const active = section === m.id;
            return (
              <button key={m.id} data-testid={`nav-${m.id}`} onClick={() => { setSection(m.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-[9px] rounded-lg text-[13px] transition-all duration-150 ${active ? 'bg-white/[0.12] text-white font-semibold' : 'text-white/50 hover:bg-white/[0.06] hover:text-white/80'}`}>
                <m.icon className="w-[17px] h-[17px] flex-shrink-0" strokeWidth={active ? 2 : 1.5} />
                <span className="truncate">{m.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Bottom: user + logout */}
        <div className="p-4 border-t border-white/[0.08]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-[11px] font-bold text-white bg-white/[0.1] flex-shrink-0">
              {user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-white/90 truncate">{user?.full_name}</p>
              <p className="text-[10px] text-white/30 truncate">{user?.email}</p>
            </div>
            <button onClick={logout} className="p-1.5 rounded-lg hover:bg-white/[0.08] transition-colors" data-testid="sidebar-logout" title="Sign out">
              <LogOut className="w-[15px] h-[15px] text-white/30 hover:text-white/60" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 z-30 bg-black/30 backdrop-blur-sm lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* ── MAIN ── */}
      <div className="flex-1 lg:ml-[260px]">
        <header className="sticky top-0 z-20 bg-white/98 backdrop-blur-md border-b border-gray-200 shadow-[0_1px_3px_rgba(0,0,0,0.06)]" data-testid="main-header">
          <div className="h-[56px] px-5 lg:px-7 flex items-center justify-between gap-4">
            {/* Left — Breadcrumbs */}
            <div className="flex items-center gap-3 min-w-0">
              <button className="lg:hidden p-1.5 rounded-lg hover:bg-gray-100 -ml-1 flex-shrink-0" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" /></svg>
              </button>
              <button onClick={() => setSection('overview')} className="hidden sm:flex items-center gap-1.5 text-gray-500 hover:text-gray-700 transition-colors flex-shrink-0 group" data-testid="breadcrumb-root">
                <Building2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span className="text-[12px] font-medium group-hover:underline underline-offset-2">{t.name}</span>
              </button>
              <ChevronRight className="w-3 h-3 text-gray-300 flex-shrink-0 hidden sm:block" />
              {(() => {
                const activeMenu = MENU.find(m => m.id === section);
                const Icon = activeMenu?.icon;
                return (
                  <div className="flex items-center gap-2 min-w-0">
                    {Icon && <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: `${A}10` }}><Icon className="w-3.5 h-3.5" style={{ color: A }} strokeWidth={2} /></div>}
                    <h2 className="text-[15px] font-bold text-gray-900 truncate" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.01em' }}>{activeMenu?.label}</h2>
                  </div>
                );
              })()}
            </div>

            {/* Right — Search, Refresh, User */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <SpotlightSearch onNavigate={setSection} accent={A} />
              <div className="w-px h-5 bg-gray-200/70 hidden sm:block" />
              <button onClick={loadDashboard} className="p-[7px] hover:bg-gray-100 rounded-lg transition-all active:scale-95" data-testid="refresh-btn" title="Refresh">
                <RefreshCw className="w-4 h-4 text-gray-500" strokeWidth={1.5} />
              </button>
              <div className="w-px h-5 bg-gray-200/70 hidden sm:block" />
              <div className="hidden sm:flex items-center gap-2.5">
                <div className="text-right">
                  <p className="text-[12px] font-semibold text-gray-900 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{user?.full_name}</p>
                  <p className="text-[10px] text-gray-500 leading-tight font-medium">{user?.role === 'tenant_owner' ? 'Admin' : 'Manager'}</p>
                </div>
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0 ring-2 ring-white shadow" style={{ background: A }} data-testid="user-avatar">
                  {user?.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
              </div>
              {branding.logo_url && (
                <>
                  <div className="w-px h-5 bg-gray-200/70 hidden sm:block" />
                  <div className="h-8 w-8 rounded-full overflow-hidden border border-gray-200 flex-shrink-0 shadow" data-testid="header-institution-logo">
                    <img src={branding.logo_url} alt="" className="w-full h-full object-contain p-0.5" />
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        <main className={`p-6 md:p-8 max-w-6xl ${['licensing','teachers','students','invoices','teacher-desk'].includes(section) ? 'overflow-hidden' : ''}`}>

          {/* ─── OVERVIEW ─── */}
          {section === 'overview' && (
            <div className="space-y-7" data-testid="overview-section">
              {/* Greeting */}
              <div>
                <h1 className="text-[22px] font-extrabold text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>Welcome back, {user?.full_name?.split(' ')[0]}</h1>
                <p className="text-sm text-gray-500 mt-1 font-medium" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Here's an overview of <span className="font-bold text-gray-700">{t.name}</span></p>
              </div>

              {/* Stat Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Active Teachers', val: s.active_teachers, icon: BookOpen, c: A, click: () => setSection('teachers') },
                  { label: 'Active Students', val: s.active_students, icon: GraduationCap, c: '#3B82F6', click: () => setSection('students') },
                  { label: 'Departments', val: s.departments || 0, icon: Building2, c: '#8B5CF6', click: () => setSection('departments') },
                  { label: 'Pending Approvals', val: s.pending_approvals, icon: Clock, c: s.pending_approvals > 0 ? '#DC2626' : '#6B7280', click: () => setSection('licensing') },
                ].map((c, i) => (
                  <div key={i} onClick={c.click} className="bg-white rounded-xl border border-gray-200 p-5 cursor-pointer transition-all duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 group" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' }} data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g,'-')}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${c.c}14` }}>
                        <c.icon className="w-[22px] h-[22px]" style={{ color: c.c }} />
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <p className="text-[28px] font-extrabold text-gray-900 tabular-nums leading-none" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>{c.val}</p>
                    <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mt-2" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{c.label}</p>
                  </div>
                ))}
              </div>

              {/* Pending Alert */}
              {s.pending_approvals > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:bg-red-100/50 transition-colors" onClick={() => setSection('licensing')} data-testid="pending-alert" style={{ boxShadow: '0 1px 3px rgba(220,38,38,0.1)' }}>
                  <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-red-900">{s.pending_approvals} licence request{s.pending_approvals > 1 ? 's' : ''} pending</p>
                    <p className="text-xs text-red-600 font-medium mt-0.5">Review and approve to grant access</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-red-400" />
                </div>
              )}

              {/* Two Column: Institution Info + Quick Actions */}
              <div className="grid lg:grid-cols-5 gap-5">
                {/* Institution Details */}
                <div className="lg:col-span-3 bg-white rounded-xl border border-gray-200 p-6" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' }}>
                  <h3 className="text-[15px] font-extrabold text-gray-900 mb-5" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.01em' }}>Institution Details</h3>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                    {[
                      { label: 'Plan', value: t.plan_name || 'Free', icon: Briefcase },
                      { label: 'Type', value: t.tenant_type, icon: Building2, capitalize: true },
                      { label: 'Institution Code', value: t.institution_code, icon: Key, mono: true, copyable: true },
                      { label: 'Login Page', value: `/${t.slug}`, icon: Globe, link: true },
                      { label: 'Total Users', value: s.total_users, icon: Users },
                      { label: 'AI Lessons', value: s.lessons || 0, icon: Sparkles },
                      { label: 'Devices', value: s.devices || 0, icon: Monitor },
                      { label: 'Published Materials', value: s.published_lessons || 0, icon: FileText },
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                          <item.icon className="w-4 h-4 text-gray-500" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{item.label}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            {item.link ? (
                              <a href={`/${t.slug}`} target="_blank" rel="noreferrer" className="text-sm font-bold hover:underline truncate" style={{ color: A }}>/{t.slug}</a>
                            ) : (
                              <span className={`text-sm font-bold text-gray-900 truncate ${item.mono ? 'font-mono' : ''} ${item.capitalize ? 'capitalize' : ''}`}>{item.value}</span>
                            )}
                            {item.copyable && <button onClick={() => copy(t.institution_code)} className="text-gray-400 hover:text-gray-600" data-testid="copy-code-btn"><Copy className="w-3.5 h-3.5" /></button>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="lg:col-span-2 space-y-4">
                  {[
                    { title: 'Manage Licensing', desc: 'Approve requests, revoke access', icon: Shield, section: 'licensing', color: A },
                    { title: 'Teacher Desk', desc: 'View teacher performance & content', icon: Briefcase, section: 'teacher-desk', color: '#8B5CF6' },
                    { title: 'Device Management', desc: 'Track and manage devices', icon: Monitor, section: 'devices', color: '#3B82F6' },
                    { title: 'Settings', desc: 'Branding, security, domain', icon: Settings, section: 'settings', color: '#6B7280' },
                  ].map((action, i) => (
                    <div key={i} onClick={() => setSection(action.section)} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4 cursor-pointer transition-all duration-200 hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 group" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' }} data-testid={`action-${action.section}`}>
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${action.color}14` }}>
                        <action.icon className="w-5 h-5" style={{ color: action.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-900">{action.title}</p>
                        <p className="text-[11px] text-gray-500 mt-0.5 font-medium">{action.desc}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-gray-500 transition-colors" />
                    </div>
                  ))}
                </div>
              </div>

            </div>
          )}

          {/* ─── LICENSING ─── */}
          {section === 'licensing' && <LicensingSection accent={A} departments={licensingDepts} />}

          {/* ─── SUBJECTS ─── */}
          {/* ─── DEPARTMENTS & CLASSES ─── */}
          {section === 'departments' && <DepartmentsSection accent={A} showToast={showToast} />}

          {/* ─── INVOICES ─── */}
          {section === 'invoices' && <InvoicesSection accent={A} />}

          {/* ─── DEVICES ─── */}
          {section === 'devices' && <DeviceManagementSection accent={A} />}

          {/* ─── TEACHER DESK ─── */}
          {section === 'teacher-desk' && <TeacherDeskSection accent={A} />}

          {/* ─── DESKEY HUB ─── */}
          {section === 'deskey-hub' && <DeskeyHubSection accent={A} mode="institution" />}

          {/* ─── TEACHERS ─── */}
          {section === 'teachers' && <TeachersSection teachers={teachers} accent={A} onRevoke={handleRevoke} />}

          {/* ─── STUDENTS ─── */}
          {section === 'students' && <StudentsSection students={students} departments={studentDepts} batches={studentBatches} accent={A} onRevoke={handleRevoke} onBlock={(id) => { loadStudents(); }} onUnblock={(id) => { loadStudents(); }} />}

          {/* ─── SUPPORT TICKETS ─── */}
          {section === 'support-tickets' && <SupportTicketsSection accent={A} />}

          {/* ─── SUBSTITUTIONS ─── */}
          {/* Substitutions removed */}

          {/* ─── SETTINGS (Combined with 2FA Gate) ─── */}
          {section === 'settings' && (
            !settingsUnlocked ? (
              <div className="max-w-md mx-auto mt-12" data-testid="settings-2fa-gate">
                <Card className="text-center">
                  <Lock className="w-12 h-12 text-gray-200 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-gray-900 mb-1">Verify Your Identity</h3>
                  <p className="text-sm text-gray-400 mb-5">Enter a 2FA code to access Settings</p>
                  {!twoFASent ? (
                    <Button onClick={sendSettingsCode} disabled={twoFASending} className="h-10 px-6 rounded-lg text-white text-sm font-semibold" style={{ background: A }} data-testid="send-settings-2fa-btn">{twoFASending ? 'Sending...' : 'Send Verification Code'}</Button>
                  ) : (
                    <div className="space-y-3">
                      <Input value={twoFACode} onChange={e => setTwoFACode(e.target.value)} placeholder="Enter 6-digit code" className="h-10 rounded-lg text-sm text-center" data-testid="settings-2fa-input" />
                      {twoFADevCode && <p className="text-xs text-orange-500">(Dev mode: {twoFADevCode})</p>}
                      <Button onClick={verifySettingsCode} disabled={twoFAVerifying || !twoFACode} className="h-10 px-6 rounded-lg text-white text-sm font-semibold w-full" style={{ background: A }} data-testid="verify-settings-2fa-btn">{twoFAVerifying ? 'Verifying...' : 'Unlock Settings'}</Button>
                    </div>
                  )}
                </Card>
              </div>
            ) : (
              <div className="space-y-6" data-testid="settings-section">
                <SectionTitle title="Settings" desc="Manage your institution's configuration" />
                {/* Sub-tabs */}
                <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
                  {SETTINGS_TABS.map(tab => (
                    <button key={tab.id} onClick={() => setSettingsTab(tab.id)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${settingsTab === tab.id ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                      data-testid={`settings-tab-${tab.id}`}>{tab.label}</button>
                  ))}
                </div>

                {/* General Info */}
                {settingsTab === 'general' && (
                  <div className="space-y-5 max-w-xl">
                    <Card>
                      <h4 className="text-base font-bold text-gray-900 mb-4">Institution Logo</h4>
                      <div className="flex items-start gap-5">
                        {branding.logo_url ? (
                          <div className="relative group"><img src={branding.logo_url} alt="Logo" className="w-20 h-20 rounded-2xl object-contain border border-gray-100 p-2" data-testid="settings-logo-preview" /><button onClick={handleLogoRemove} className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid="remove-logo-btn"><Trash2 className="w-3 h-3" /></button></div>
                        ) : (<div className="w-20 h-20 rounded-2xl border-2 border-dashed border-gray-200 flex items-center justify-center"><Building2 className="w-8 h-8 text-gray-200" /></div>)}
                        <div><label className="cursor-pointer"><input type="file" accept="image/*" className="hidden" data-testid="logo-upload-input" onChange={e => handleLogoUpload(e.target.files?.[0])} /><div className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors w-fit"><Upload className="w-4 h-4 text-gray-500" /><span className="text-xs font-medium text-gray-600">{branding.logo_url ? 'Change Logo' : 'Upload Logo'}</span></div></label><p className="text-[10px] text-gray-400 mt-2">PNG, JPG, SVG. Max 2MB.</p></div>
                      </div>
                    </Card>
                    <Card>
                      <h4 className="text-base font-bold text-gray-900 mb-4">Institution Details</h4>
                      <div className="space-y-3">
                        <div><label className="block text-sm text-gray-500 mb-1">Institution Name</label><Input value={general.name || ''} onChange={e => setGeneral(p => ({ ...p, name: e.target.value }))} className="h-10 rounded-lg text-sm" data-testid="general-name" /></div>
                        <div><label className="block text-sm text-gray-500 mb-1">Type</label><select value={general.institution_type || 'school'} onChange={e => setGeneral(p => ({ ...p, institution_type: e.target.value }))} className="w-full h-10 rounded-lg border border-gray-200 text-sm px-3 bg-white" data-testid="general-type"><option value="school">School</option><option value="coaching">Coaching Center</option><option value="university">University</option></select></div>
                        <div><label className="block text-sm text-gray-500 mb-1">Phone</label><Input value={general.phone || ''} onChange={e => setGeneral(p => ({ ...p, phone: e.target.value }))} placeholder="+91 98765 43210" className="h-10 rounded-lg text-sm" data-testid="general-phone" /></div>
                        <div><label className="block text-sm text-gray-500 mb-1">Website</label><Input value={general.website || ''} onChange={e => setGeneral(p => ({ ...p, website: e.target.value }))} placeholder="https://www.school.edu" className="h-10 rounded-lg text-sm" data-testid="general-website" /></div>
                        <div><label className="block text-sm text-gray-500 mb-1">Address</label><Input value={general.address || ''} onChange={e => setGeneral(p => ({ ...p, address: e.target.value }))} placeholder="City, State" className="h-10 rounded-lg text-sm" data-testid="general-address" /></div>
                      </div>
                      {renderSaveWithTwoFA("Save Changes", doGeneralSave, generalSaving, "save-general-btn")}
                    </Card>
                  </div>
                )}

                {/* Appearance */}
                {settingsTab === 'appearance' && (
                  <div className="space-y-5 max-w-3xl">
                    <Card>
                      <h4 className="text-base font-bold text-gray-900 mb-1">Theme Presets</h4>
                      <p className="text-xs text-gray-400 mb-4">Select a preset or create your own custom theme</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                        {DASHBOARD_THEMES.map(th => {
                          const sel = !isCustomTheme && selectedThemeId === th.id;
                          return (
                            <div key={th.id} onClick={() => selectTheme(th)} className="relative cursor-pointer rounded-xl p-2.5 transition-all hover:shadow" style={{ border: sel ? `2px solid ${th.accent}` : '2px solid #f3f4f6', background: sel ? `${th.accent}08` : '#fff' }} data-testid={`theme-${th.id}`}>
                              {sel && <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center shadow z-10" style={{ background: th.accent }}><Check className="w-3 h-3 text-white" /></div>}
                              <div className="h-14 rounded-lg overflow-hidden flex mb-2 border border-gray-100">
                                <div className="w-[30%] flex flex-col items-center justify-center gap-1 py-2" style={{ background: th.sidebar }}><div className="w-4 h-1 rounded-full bg-white/20" /><div className="w-4 h-1 rounded-full bg-white/40" /><div className="w-4 h-1 rounded-full bg-white/20" /></div>
                                <div className="flex-1 bg-gray-50 flex flex-col justify-center px-2 gap-1"><div className="h-1 w-full bg-gray-200/60 rounded-full" /><div className="h-1 w-3/4 bg-gray-200/60 rounded-full" /><div className="h-2.5 w-1/2 rounded" style={{ background: th.accent }} /></div>
                              </div>
                              <p className="text-xs font-medium text-gray-700 text-center">{th.name}</p>
                            </div>
                          );
                        })}
                        <div onClick={selectCustom} className="relative cursor-pointer rounded-xl p-2.5 transition-all hover:shadow" style={{ border: isCustomTheme ? `2px solid ${customAccent}` : '2px solid #f3f4f6', background: isCustomTheme ? `${customAccent}08` : '#fff' }} data-testid="theme-custom">
                          {isCustomTheme && <div className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center shadow z-10" style={{ background: customAccent }}><Check className="w-3 h-3 text-white" /></div>}
                          <div className="h-14 rounded-lg overflow-hidden flex mb-2 border-2 border-dashed border-gray-200 items-center justify-center bg-gray-50/50"><Paintbrush className="w-5 h-5 text-gray-400" /></div>
                          <p className="text-xs font-medium text-gray-700 text-center">Custom</p>
                        </div>
                      </div>
                    </Card>
                    {isCustomTheme && (
                      <Card>
                        <h4 className="text-base font-bold text-gray-900 mb-4">Custom Colors</h4>
                        <div className="grid grid-cols-2 gap-6">
                          <div><label className="block text-sm text-gray-500 mb-1.5">Sidebar Color</label><div className="flex items-center gap-3"><input type="color" value={customSidebar} onChange={e => updateCustomSidebar(e.target.value)} className="w-10 h-10 rounded-lg border-0 cursor-pointer" data-testid="custom-sidebar-color" /><span className="text-xs text-gray-400 font-mono">{customSidebar}</span></div></div>
                          <div><label className="block text-sm text-gray-500 mb-1.5">Accent Color</label><div className="flex items-center gap-3"><input type="color" value={customAccent} onChange={e => updateCustomAccent(e.target.value)} className="w-10 h-10 rounded-lg border-0 cursor-pointer" data-testid="custom-accent-color" /><span className="text-xs text-gray-400 font-mono">{customAccent}</span></div></div>
                        </div>
                        <div className="mt-4 h-16 rounded-xl overflow-hidden flex border border-gray-100"><div className="w-[30%] flex flex-col items-center justify-center gap-1 py-2" style={{ background: customSidebar }}><div className="w-5 h-1 rounded-full bg-white/20" /><div className="w-5 h-1 rounded-full bg-white/40" /><div className="w-5 h-1 rounded-full bg-white/20" /></div><div className="flex-1 bg-gray-50 flex flex-col justify-center px-3 gap-1"><div className="h-1 w-full bg-gray-200 rounded-full" /><div className="h-1 w-3/4 bg-gray-200 rounded-full" /><div className="h-3 w-1/2 rounded" style={{ background: customAccent }} /></div></div>
                      </Card>
                    )}
                    {renderSaveWithTwoFA("Save Theme", doThemeSave, themeSaving, "save-theme-btn")}
                  </div>
                )}

                {/* Login Page Branding */}
                {settingsTab === 'branding' && (
                  <div className="space-y-5 max-w-xl">
                    <Card>
                      <h4 className="text-base font-bold text-gray-900 mb-1">Quick Themes</h4>
                      <p className="text-xs text-gray-400 mb-3">Click a preset to apply, then fine-tune colors below</p>
                      <div className="grid grid-cols-5 gap-2">
                        {LOGIN_THEMES.map(lt => {
                          const active = isLoginPresetMatch(lt);
                          return (
                            <div key={lt.id} onClick={() => applyLoginPreset(lt)} className="relative cursor-pointer rounded-lg p-1.5 transition-all hover:shadow" style={{ border: active ? `2px solid ${lt.primary_color}` : '2px solid #f3f4f6' }} data-testid={`login-theme-${lt.id}`}>
                              {active && <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center shadow z-10" style={{ background: lt.primary_color }}><Check className="w-2.5 h-2.5 text-white" /></div>}
                              <div className="h-12 rounded overflow-hidden flex"><div className="w-[40%] flex flex-col justify-end p-1" style={{ background: lt.bg_color }}><div className="h-0.5 w-3/4 bg-white/30 rounded-full" /><div className="h-0.5 w-1/2 bg-white/20 rounded-full mt-0.5" /></div><div className="flex-1 bg-white flex flex-col items-center justify-center px-1 gap-0.5"><div className="h-0.5 w-full bg-gray-100 rounded-full" /><div className="h-2 w-3/4 rounded" style={{ background: lt.primary_color }} /></div></div>
                              <p className="text-[9px] font-medium text-gray-500 text-center mt-1 truncate">{lt.name}</p>
                            </div>
                          );
                        })}
                      </div>
                    </Card>
                    <Card>
                      <h4 className="text-base font-bold text-gray-900 mb-3">Preview</h4>
                      <div className="rounded-xl border border-gray-100 overflow-hidden flex" style={{ height: 140 }}>
                        <div className="w-[45%] flex flex-col items-start justify-center p-5 relative overflow-hidden" style={{ background: branding.bg_color || '#14532d' }}>
                          <div className="absolute -top-6 -right-6 w-16 h-16 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }} />
                          {branding.logo_url ? <img src={branding.logo_url} alt="" className="w-8 h-8 rounded-md object-contain mb-2" /> : <div className="w-8 h-8 rounded-md flex items-center justify-center mb-2" style={{ background: 'rgba(255,255,255,0.1)' }}><Building2 className="w-4 h-4 text-white" /></div>}
                          <p className="text-xs font-bold text-white truncate max-w-full">{branding.login_title || t.name}</p>
                          <p className="text-[8px] text-white/60 mt-0.5 truncate max-w-full">{branding.login_subtitle?.slice(0, 50) || 'Sign in to your account'}</p>
                        </div>
                        <div className="flex-1 bg-white flex flex-col items-center justify-center gap-1.5 p-4"><div className="w-full h-2 bg-gray-100 rounded-full" /><div className="w-full h-2 bg-gray-100 rounded-full" /><div className="w-full h-7 rounded-lg mt-1.5" style={{ background: branding.primary_color || A }} /></div>
                      </div>
                    </Card>
                    <Card>
                      <h4 className="text-base font-bold text-gray-900 mb-4">Content</h4>
                      <div className="space-y-3">
                        <div><label className="block text-sm text-gray-500 mb-1">Title</label><Input value={branding.login_title || ''} onChange={e => setBranding(p => ({ ...p, login_title: e.target.value }))} placeholder={t.name} className="h-10 rounded-lg text-sm" data-testid="branding-title" /></div>
                        <div><label className="block text-sm text-gray-500 mb-1">Subtitle</label><Input value={branding.login_subtitle || ''} onChange={e => setBranding(p => ({ ...p, login_subtitle: e.target.value }))} placeholder="One platform to power every classroom..." className="h-10 rounded-lg text-sm" data-testid="branding-subtitle" /></div>
                        <div><label className="block text-sm text-gray-500 mb-1">Footer Text</label><Input value={branding.footer_text || ''} onChange={e => setBranding(p => ({ ...p, footer_text: e.target.value }))} className="h-10 rounded-lg text-sm" data-testid="branding-footer" /></div>
                      </div>
                    </Card>
                    <Card>
                      <h4 className="text-base font-bold text-gray-900 mb-4">Colors</h4>
                      <div className="grid grid-cols-3 gap-4">
                        <div><label className="block text-sm text-gray-500 mb-1">Left Panel BG</label><div className="flex items-center gap-2"><input type="color" value={branding.bg_color || '#14532d'} onChange={e => setBranding(p => ({ ...p, bg_color: e.target.value }))} className="w-10 h-10 rounded-lg border-0 cursor-pointer" data-testid="branding-bg-color" /><span className="text-[10px] text-gray-400 font-mono">{branding.bg_color || '#14532d'}</span></div></div>
                        <div><label className="block text-sm text-gray-500 mb-1">Button Color</label><div className="flex items-center gap-2"><input type="color" value={branding.primary_color || '#34A853'} onChange={e => setBranding(p => ({ ...p, primary_color: e.target.value }))} className="w-10 h-10 rounded-lg border-0 cursor-pointer" data-testid="branding-primary-color" /><span className="text-[10px] text-gray-400 font-mono">{branding.primary_color || '#34A853'}</span></div></div>
                        <div><label className="block text-sm text-gray-500 mb-1">Accent</label><div className="flex items-center gap-2"><input type="color" value={branding.accent_color || '#f97316'} onChange={e => setBranding(p => ({ ...p, accent_color: e.target.value }))} className="w-10 h-10 rounded-lg border-0 cursor-pointer" data-testid="branding-accent-color" /><span className="text-[10px] text-gray-400 font-mono">{branding.accent_color || '#f97316'}</span></div></div>
                      </div>
                    </Card>
                    <div className="flex items-center gap-3">
                      {renderSaveWithTwoFA("Save Changes", doBrandingSave, brandingSaving, "save-branding-btn")}
                      {t.slug && <a href={`/${t.slug}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs font-medium hover:underline ml-4" style={{ color: A }} data-testid="preview-login-btn"><ExternalLink className="w-3.5 h-3.5" /> Preview Login Page</a>}
                    </div>
                  </div>
                )}

                {/* Domain */}
                {settingsTab === 'domain' && (
                  <div className="space-y-5 max-w-xl">
                    <Card>
                      <h4 className="text-base font-bold text-gray-900 mb-3">Default Login URL</h4>
                      <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-4 py-3"><Link2 className="w-4 h-4 text-gray-400 flex-shrink-0" /><span className="text-sm text-gray-700 font-mono">{window.location.origin}/{t.slug}</span><button onClick={() => copy(`${window.location.origin}/${t.slug}`)} className="ml-auto text-gray-400 hover:text-gray-600"><Copy className="w-3.5 h-3.5" /></button></div>
                      <p className="text-[10px] text-gray-400 mt-2">This URL is always available, even with a custom domain.</p>
                    </Card>
                    <Card>
                      <h4 className="text-base font-bold text-gray-900 mb-3">{t.custom_domain ? 'Your Custom Domain' : 'Set Custom Domain'}</h4>
                      {t.custom_domain ? (
                        <div>
                          <div className="flex items-center gap-3 bg-gray-50 rounded-lg px-4 py-3 mb-3"><Globe className="w-4 h-4 flex-shrink-0" style={{ color: t.domain_verified ? A : '#F59E0B' }} /><span className="text-sm font-medium text-gray-900">{t.custom_domain}</span><span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ml-auto ${t.domain_verified ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>{t.domain_verified ? 'Verified' : 'Pending'}</span></div>
                          <div className="flex gap-2">{!t.domain_verified && <Button onClick={handleDomainVerify} disabled={domainVerifying} size="sm" className="h-8 px-3 text-xs rounded-lg text-white" style={{ background: A }}>{domainVerifying ? 'Verifying...' : 'Verify Now'}</Button>}<Button onClick={handleDomainRemove} size="sm" variant="outline" className="h-8 px-3 text-xs rounded-lg text-red-500 border-red-200">Remove</Button></div>
                        </div>
                      ) : (
                        <div className="flex gap-2 mb-3"><Input value={domain} onChange={e => setDomain(e.target.value)} placeholder="login.yourschool.com" className="h-10 rounded-lg text-sm flex-1" data-testid="domain-input" /><Button onClick={() => initSave2FA(doDomainSave, 'save-domain-btn')} disabled={domainSaving || !domain} className="h-10 px-5 rounded-lg text-white text-sm font-semibold" style={{ background: A }} data-testid="save-domain-btn">{domainSaving ? 'Saving...' : 'Save'}</Button></div>
                      )}
                      {pendingSaveId === 'save-domain-btn' && (
                        <div className="flex items-center gap-2 mt-3 p-3 rounded-xl bg-amber-50 border border-amber-200">
                          <Lock className="w-4 h-4 text-amber-600 flex-shrink-0" />
                          <Input value={saveCode} onChange={e => setSaveCode(e.target.value)} placeholder="6-digit code" className="h-8 w-32 text-sm rounded-lg" />
                          <Button onClick={confirmSave} disabled={saveVerifying || !saveCode} size="sm" className="h-8 px-3 text-xs rounded-lg text-white" style={{ background: A }}>Confirm</Button>
                          <button onClick={() => setPendingSave(null)} className="text-xs text-gray-400">Cancel</button>
                          {saveDevCode && <span className="text-[10px] text-orange-500 ml-1">(Dev: {saveDevCode})</span>}
                        </div>
                      )}
                    </Card>
                    <Card>
                      <div className="flex items-start gap-3 mb-4"><Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" /><div><h4 className="text-sm font-semibold text-gray-900">How to set up your custom domain</h4><p className="text-xs text-gray-400 mt-1">Follow these steps to point your domain to your Deskey login page</p></div></div>
                      <div className="space-y-4">
                        {[
                          { n: 1, t: 'Sign in to your DNS provider', d: 'Go to your domain registrar (GoDaddy, Namecheap, Cloudflare, etc.) and open DNS settings.' },
                          { n: 2, t: 'Add a CNAME record', d: null },
                          { n: 3, t: 'Wait for DNS propagation', d: 'DNS changes can take up to 24-48 hours. Usually under 1 hour.' },
                          { n: 4, t: 'Verify your domain', d: 'Come back here and click "Verify Now". We\'ll check the CNAME record.' },
                        ].map(step => (
                          <div key={step.n} className="flex gap-3">
                            <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background: A }}>{step.n}</div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{step.t}</p>
                              {step.d && <p className="text-xs text-gray-400 mt-0.5">{step.d}</p>}
                              {step.n === 2 && (<div className="mt-2 bg-gray-50 rounded-lg p-3 text-xs font-mono space-y-1.5"><div className="flex"><span className="text-gray-400 w-16">Type:</span><span className="text-gray-900 font-semibold">CNAME</span></div><div className="flex"><span className="text-gray-400 w-16">Host:</span><span className="text-gray-900 font-semibold">{domain || 'login'}</span></div><div className="flex"><span className="text-gray-400 w-16">Value:</span><span className="font-semibold" style={{ color: A }}>{t.slug}.deskey.app</span></div><div className="flex"><span className="text-gray-400 w-16">TTL:</span><span className="text-gray-900">3600 (or Auto)</span></div></div>)}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-5 pt-4 border-t border-gray-100"><p className="text-[10px] text-gray-400">Having trouble? Contact <a href="mailto:support@deskey.app" className="underline" style={{ color: A }}>support@deskey.app</a></p></div>
                    </Card>
                  </div>
                )}
              </div>
            )
          )}

          {/* ─── SECURITY ─── */}
          {section === 'security' && (
            <div className="space-y-6 max-w-xl" data-testid="security-section">
              <SectionTitle title="Security Settings" desc="Configure two-factor authentication for your institution" />
              <Card>
                <h4 className="text-base font-bold text-gray-900 mb-2">Two-Factor Authentication (2FA)</h4>
                <p className="text-xs text-gray-400 mb-5">When enabled, users must verify via a 6-digit email code on every login.</p>
                <div className="space-y-3">
                  {[
                    { key: 'institution', label: 'Institution Admins', desc: 'Require 2FA for all admin logins', recommended: true },
                    { key: 'teacher', label: 'Teachers', desc: 'Require 2FA for teacher logins', recommended: true },
                    { key: 'student', label: 'Students', desc: 'Require 2FA for student logins', recommended: false },
                  ].map(item => (
                    <div key={item.key} className="flex items-center justify-between p-4 rounded-xl border border-gray-100 hover:bg-gray-50/50 transition-colors" data-testid={`2fa-${item.key}`}>
                      <div>
                        <div className="flex items-center gap-2"><p className="text-sm font-medium text-gray-900">{item.label}</p>{item.recommended && <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: `${A}15`, color: A }}>Recommended</span>}</div>
                        <p className="text-xs text-gray-400 mt-0.5">{item.desc}</p>
                      </div>
                      <Toggle checked={twoFA[item.key]} onChange={v => setTwoFA(p => ({ ...p, [item.key]: v }))} testId={`2fa-toggle-${item.key}`} accent={A} />
                    </div>
                  ))}
                </div>
                <div className="mt-5 pt-4 border-t border-gray-100 flex items-start gap-2"><Shield className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" /><p className="text-[10px] text-gray-400">2FA codes are sent via email and expire after 5 minutes.</p></div>
                <Button onClick={handleTwoFASave} disabled={twofaSaving} className="mt-4 h-10 px-6 rounded-lg text-white text-sm font-semibold" style={{ background: A }} data-testid="save-2fa-btn">{twofaSaving ? 'Saving...' : 'Save Security Settings'}</Button>
              </Card>
            </div>
          )}

        </main>
      </div>
    </div>
  );
}
